<template>
  <div>
    <ra-card v-loading="loading">
      <template slot="header">
        <div class="header">
          <p>{{ total }} Members</p>
          <el-button
            v-if="setPermission('invite')"
            type="primary"
            size="small"
            @click="setMemberAction({ action: 'add' })"
            >Add New</el-button
          >
        </div>
      </template>
      <template slot="content">
        <el-table :data="pageData" :empty-text="'No member'">
          <el-table-column prop="name">
            <template slot="header">
              <span class="ra-table--header">
                Name
              </span>
            </template>
            <template v-slot="scope">
              <p v-if="scope.row.account_user">
                {{ scope.row.account_user.first_name }}
                {{ scope.row.account_user.last_name }}
              </p>
              <el-tag v-if="scope.row.is_owner" size="mini" effect="dark">
                Owner
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="identifier" min-width="120">
            <template slot="header">
              <span class="ra-table--header">
                Email
              </span>
            </template>
            <template v-slot="scope">
              <p>
                {{ scope.row.account_user && scope.row.account_user.email }}
              </p>
            </template>
          </el-table-column>
          <el-table-column v-if="false" prop="role" width="200">
            <template slot="header">
              <span class="ra-table--header">
                Role
              </span>
            </template>
            <template v-slot="scope">
              <div v-if="scope.row.user_roles && scope.row.user_roles.length">
                <el-tag
                  v-for="role in scope.row.user_roles"
                  :key="role.id"
                  type="info"
                  size="mini"
                  >{{ role.role && role.role.description }}</el-tag
                >
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="role" width="100">
            <template slot="header">
              <span class="ra-table--header">
                Roles
              </span>
            </template>
            <template v-slot="scope">
              <div
                v-if="scope.row.user_roles && scope.row.user_roles.length > 1"
              >
                <el-popover placement="top-end" trigger="click">
                  <ul class="roles">
                    <li
                      v-for="(role, i) in scope.row.user_roles"
                      :key="i"
                      style="font-size: 12px"
                    >
                      {{ role.role.description }}
                    </li>
                  </ul>
                  <p slot="reference" class="text-primary text-underline">
                    {{ scope.row.user_roles.length }} roles
                  </p>
                </el-popover>
              </div>
              <p v-else-if="scope.row.user_roles.length === 1">
                {{ scope.row.user_roles[0].role.description }}
              </p>
            </template>
          </el-table-column>
          <el-table-column>
            <template slot="header">
              <span class="ra-table--header">
                Added
              </span>
            </template>
            <template v-slot="scope">
              <p>
                {{ mFormatDate(scope.row.created_at, "Do MMM, YYYY") }}
              </p>
            </template>
          </el-table-column>
          <el-table-column>
            <template slot="header">
              <span class="ra-table--header">
                Updated
              </span>
            </template>
            <template v-slot="scope">
              <p>
                {{ mFormatDate(scope.row.modified_at, "Do MMM, YYYY") }}
              </p>
            </template>
          </el-table-column>
          <el-table-column prop="status" min-width="60">
            <template slot="header">
              <span class="ra-table--header">
                Status
              </span>
            </template>
            <template v-slot="scope">
              <el-tag
                :type="scope.row.status_id === 1 ? 'success' : 'warning'"
                size="mini"
              >
                {{ scope.row.status && scope.row.status.label }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="setPermission('role')" width="60">
            <template v-slot="scope">
              <el-dropdown
                v-if="!scope.row.is_owner"
                @command="setMemberAction"
                trigger="click"
              >
                <i class="ra-icon--more-vertical" />
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    v-if="scope.row.status_id === 10 && setPermission('invite')"
                    :command="{ action: 'resend', member: scope.row }"
                    >Resend invite</el-dropdown-item
                  >
                  <el-dropdown-item
                    v-if="setPermission('role')"
                    :command="{ action: 'change-role', member: scope.row }"
                    >Change role</el-dropdown-item
                  >
                  <el-dropdown-item
                    v-if="setPermission('remove')"
                    :command="{ action: 'remove', member: scope.row }"
                    >Remove</el-dropdown-item
                  >
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
        <ra-pagination
          v-if="dataFound"
          :from="from"
          :to="to"
          :total="total"
          :pageSize="pagination.perPage"
          :current-page.sync="currentPage"
        />
      </template>
    </ra-card>
    <component
      :is="memberAction.component"
      :show.sync="memberAction.visibility"
      :member="memberAction.member"
      @updated="getOrganisationMembers"
    />
  </div>
</template>

<script>
import { userCan } from "@/lib/utils";

const paging = {
  perPage: 20,
  total: 0
};

export default {
  name: "OrganisationMembers",
  props: {
    organisationId: {
      type: Number,
      required: true
    }
  },
  components: {
    OrganisationMemberInvite: () => import("./OrganisationMemberInvite.vue"),
    OrganisationMemberRoleChange: () =>
      import("./OrganisationMemberRoleChange.vue")
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      pagination: {
        ...paging
      },
      pageData: [],
      showMemberInvite: false,
      memberAction: {
        component: "",
        action: "",
        visibility: false,
        member: {}
      }
    };
  },
  computed: {
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },

    from() {
      return this.pagination.perPage * (this.currentPage - 1);
    },

    total() {
      return this.pagination.total;
    },

    dataFound() {
      return !!this.pageData.length;
    }
  },
  watch: {
    currentPage() {
      this.getOrganisationMembers();
    }
  },
  created() {
    this.getOrganisationMembers();
  },
  methods: {
    getOrganisationMembers() {
      if (!this.organisationId) {
        return;
      }
      this.loading = true;

      const apiUrl = `organisations/${this.organisationId}/users`;
      const params = `&embeds=["status", "userRoles.role"]`;
      const pagination = `page_size=${this.pagination.perPage}&page=${this.currentPage}`;

      this.$http
        .post(apiUrl, {
          method: "GET",
          params: `${params}&${pagination}`
        })
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
            this.loading = false;
          } else {
            this.pageData = resData.list;
            this.pagination.total = resData.pagination.total_count;
            this.owner = this.loading = false;
          }
        })
        .catch(() => {
          this.notification("Error", "An unknown error occurred", "danger");
          this.loading = false;
        });
    },
    setPermission(action) {
      const actions = {
        invite: "accountuser.inviteuser",
        role: "organisation.updateaccountuserrole",
        remove: "organisation.removeuser"
      };

      return userCan("auth", actions[action]);
    },
    setMemberAction(command) {
      if (command.action === "add") {
        this.memberAction.member = {};
      } else {
        this.memberAction.member = command.member;
      }

      switch (command.action) {
        case "add":
          this.memberAction.action = "add";
          this.memberAction.component = "organisation-member-invite";
          this.memberAction.visibility = true;
          break;
        case "resend":
          this.memberAction.action = "resend";
          this.memberAction.component = "organisation-member-invite";
          this.memberAction.visibility = true;
          break;
        case "remove":
          this.removeMember(command.member && command.member.account_user_id);
          break;
        case "change-role":
          this.memberAction.component = "organisation-member-role-change";
          this.memberAction.visibility = true;
          break;
        default:
          break;
      }
    },
    removeMember(memberId) {
      this.$confirm(
        "Are you sure you want to remove this member?",
        "Remove Member",
        {
          confirmButtonText: "Remove",
          cancelButtonText: "Cancel",
          type: "danger"
        }
      ).then(() => {
        const loading = this.$loading({
          lock: true,
          background: "rgba(0, 0, 0, 0.7)",
          customClass: "dark"
        });

        this.$http
          .post(
            `organisations/${this.organisationId}/accounts/users/${memberId}/remove`,
            {
              method: "PUT"
            }
          )
          .then(response => {
            const resData = response.data;
            if (resData.status === "error") {
              this.handleErrors(resData);
              loading.close();
            } else {
              this.getOrganisationMembers();
              loading.close();
              this.notification(
                "Completed",
                "Member removed successfully.",
                "success"
              );
            }
          })
          .catch(() => {
            this.notification("Error", "An unknown error occurred", "danger");
            loading.close();
          });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.roles {
  list-style-type: square;
  margin: 0;
  padding: 0;
  padding-left: 20px;

  li {
    font-size: 12px;
  }
}
</style>
